import {ChangeEvent, Dispatch, SetStateAction} from 'react';

export interface ProcedureRunSelectionForAnalysis {
  id: string;
  name: string;
}

interface ProcedureRunOption {
  id: string;
  name: string;
}

interface UseProcedureRunSelectionForAnalysisResult {
  selectedProcedureRunIdsForAnalysis: Array<string>;
  handleToggleAllProcedureRuns: (event: ChangeEvent<HTMLInputElement>) => void;
  handleToggleProcedureRun: (procedureRunId: string) => (event: ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Encapsulates checkbox selection state for procedure runs (not plans) in the listing table.
 * Only actual procedure runs may be selected; procedure run plans are excluded by design
 * since callers pass only `procedureRuns`, never plans.
 */
export function useProcedureRunSelectionForAnalysis(
  procedureRuns: Array<ProcedureRunOption>,
  selectedProcedureRunsForAnalysis: Array<ProcedureRunSelectionForAnalysis>,
  setSelectedProcedureRunsForAnalysis: Dispatch<SetStateAction<Array<ProcedureRunSelectionForAnalysis>>>
): UseProcedureRunSelectionForAnalysisResult {
  const selectedProcedureRunIdsForAnalysis = selectedProcedureRunsForAnalysis.map((run) => run.id);

  const handleToggleAllProcedureRuns = (event: ChangeEvent<HTMLInputElement>): void => {
    event.stopPropagation();
    setSelectedProcedureRunsForAnalysis(
      event.target.checked ? procedureRuns.map(({id, name}) => ({id, name})) : []
    );
  };

  const handleToggleProcedureRun =
    (procedureRunId: string) =>
    (event: ChangeEvent<HTMLInputElement>): void => {
      event.stopPropagation();
      if (event.target.checked) {
        const procedureRun = procedureRuns.find(({id}) => id === procedureRunId);
        if (procedureRun == null) {
          return;
        }
        setSelectedProcedureRunsForAnalysis((prev) => [
          ...prev,
          {id: procedureRun.id, name: procedureRun.name},
        ]);
      } else {
        setSelectedProcedureRunsForAnalysis((prev) => prev.filter(({id}) => id !== procedureRunId));
      }
    };

  return {selectedProcedureRunIdsForAnalysis, handleToggleAllProcedureRuns, handleToggleProcedureRun};
}
